import type { TranslationArg } from '../types';

/**
 * Marcador de interpolación: `{{nombre}}`, con espacios opcionales alrededor del nombre.
 *
 * Se admiten los espacios porque quien escribe el catálogo no siempre los omite, y una clave con
 * `{{ count }}` que no sustituye es indistinguible de un defecto del motor para quien la reporta.
 */
const MARCADOR = /\{\{\s*(\w+)\s*\}\}/g;

/**
 * Sustituye los marcadores `{{nombre}}` de un texto ya traducido.
 *
 * <para><b>Por qué vive aquí y no en el motor.</b> `TranslationEngine` es un contrato PÚBLICO que
 * implementan motores de terceros: ampliar su `translate` obligaría a cambiar cada implementación
 * existente. La interpolación es, además, independiente del catálogo — opera sobre el resultado—,
 * así que su sitio es la capa del provider, donde se aplica por igual a cualquier motor.</para>
 *
 * <para><b>Un marcador sin valor se deja INTACTO.</b> Borrarlo produce una frase que se lee bien y
 * miente («0 línea(s)» cuando no se sabe cuántas hay); dejarlo visible se reconoce al instante como
 * lo que es. El mismo criterio que sigue `translate` al devolver la llave cuando no la encuentra.</para>
 *
 * @example
 * interpolate('{{count}} línea(s) · saldo {{balance}}', { count: 3, balance: '265,00' })
 * // '3 línea(s) · saldo 265,00'
 */
export function interpolate(text: string, arg?: TranslationArg): string {
  // Una CADENA no interpola: es el valor por omisión, y lo aplica `resolveTranslation`, que es quien
  // sabe si la llave se encontró. Aquí sólo se deja el texto intacto.
  if (!arg || typeof arg === 'string') return text;

  return text.replace(MARCADOR, (marcador, nombre: string) => {
    const valor = arg[nombre];
    return valor === null || valor === undefined ? marcador : String(valor);
  });
}

/**
 * Resuelve el segundo argumento de `t`: interpola si es un objeto, y aplica el respaldo si es una
 * cadena y la llave no estaba en el catálogo.
 *
 * <para>El motor devuelve la PROPIA LLAVE cuando no la encuentra; ésa es la señal por la que se
 * decide aplicar el respaldo, y es la misma que el kit ya usaba antes de que `t` tuviera segundo
 * argumento.</para>
 */
export function resolveTranslation(key: string, translated: string, arg?: TranslationArg): string {
  if (typeof arg === 'string') return translated === key ? arg : translated;
  return interpolate(translated, arg);
}
